import { Auth } from '@sdk/auth';
import { createPublicAxios } from '@sdk/utils/axios';
import {
  API_ENDPOINT_DEV,
  API_ENDPOINT_MAINNET,
  API_ENDPOINT_STAGING,
  API_ENDPOINT_TESTNET,
  BASE_URL_DEV,
  BASE_URL_MAINNET,
  BASE_URL_STAGING,
  SOCKET_APP_KEY_MAINNET,
  SOCKET_APP_KEY_TESTNET,
  SOCKET_HOST_DEV,
  SOCKET_HOST_MAINNET,
  SOCKET_HOST_TESTNET,
} from '@sdk/shared/constants';
import NotificationClient from '@sdk/notification';
import { privateKeyToAccount } from 'viem/accounts';
import { Chain } from 'viem';
import { arbitrumSepolia } from 'viem/chains';
import {
  executeOrder,
  ExecuteOrderResult,
  OrderExecutorArgs,
  OrderExecutorOptions,
} from '@sdk/orderExecutor';
import { AxiosInstance } from 'axios';
import { orderService } from '@sdk/orders/order.service';
import { AdsService } from '@sdk/ads/ads.service';
import { ConfigService } from '@sdk/config/config.service';
import { AnalyticsService } from '@sdk/analytics/services/analytics.service';
import { KycService } from '@sdk/kyc/services/kyc.service';
import {
  BaseEvent,
  IxEvent,
  IxEvents,
  IxEventType,
  IxEventUnion,
  QuickTradeNotificationData,
} from '@sdk/notification/types';
import { ExtendedWalletClient } from '@sdk/auth/types';
import { KYCStatus, AuthenticateParams } from '@sdk/auth';
import { SdkConfig, IxEnv } from '@sdk/shared/types/sdk.types';
import { EIP1193Provider } from 'viem';
import {
  mainnetChains,
  mainnetChainsSponsored,
  testnetChainsSponsored,
  testnetChains,
} from '@shared/src/config/chains';
import { TypeOf } from 'zod';

export interface SdkEnvironmentVars {
  ENV: IxEnv;
  WEB3_PROJECT_ID: string;
  ALCHEMY_API_KEY: string;
  ALCHEMY_GAS_POLICY_ID: string;
  API_BASE_URL: string;
  BASE_URL: string;
  SOCKET_APP_KEY: string;
  SOCKET_HOST: string;
  CLARITY_PROJECT_ID: string;
  SPONSORED_CHAINS: Chain[];
}

export type {
  OrderExecutorArgs,
  QuickTradeNotificationData,
  ExtendedWalletClient,
  IxEvent,
  EIP1193Provider,
  KYCStatus,
  AuthenticateParams,
};
export type {
  AutoTraderStatus,
  AutoTraderApplication,
  AutoTraderStatusResponse,
  RegisterAutoTraderResponse,
  AutoTraderApplicationFilters,
  AutoTraderApplicationListResponse,
  AutoTraderStatsResponse,
} from '@sdk/shared/types/settings.types';
export { IxEventType };

/**
 * Main SDK client class.
 * Each instance manages its own authentication state and exposes all modules.
 */
export class SdkClient {
  /**
   * Authentication module instance. Handles SIWE, JWT, and wallet state.
   */
  public readonly auth: Auth;

  public readonly publicAxios: AxiosInstance;

  public readonly orders: orderService;

  public readonly notification: NotificationClient;
  /**
   * Ads module instance. Handles ad creation, updates, and queries.
   */
  public readonly ads: AdsService;
  /** Analytics service wrapper. Instantiated on demand with current auth token. */
  public get analytics(): AnalyticsService {
    return new AnalyticsService(this.auth.adminAxios);
  }
  /**
   * Config module instance. Handles network, token, and payment config queries.
   */
  public readonly config: ConfigService;

  /**
   * KYC admin service accessor. Returns a fresh instance wired to the current admin Axios
   * (and therefore the latest auth token) each time it is accessed, mirroring the
   * pattern used by the `analytics` getter.
   */
  public get kyc(): KycService {
    return new KycService(this.auth.adminAxios);
  }

  public readonly env: IxEnv;

  public readonly apiBaseUrl: string;

  public readonly baseUrl: string;

  /**
   * Environment variables derived from the configured SDK env, mirroring next-base.ts
   * Includes Alchemy keys and other runtime constants for the selected environment.
   */
  public readonly environment: SdkEnvironmentVars;

  public readonly executeOrder: (
    args: OrderExecutorArgs,
    options?: OrderExecutorOptions,
  ) => ExecuteOrderResult;

  // Subscribers for individual event types
  private readonly subscribers: {
    [K in keyof IxEvents]: Set<(event: IxEvents[K]) => void>;
  } = {
    [IxEventType.CHAT]: new Set(),
    [IxEventType.ORDER]: new Set(),
    [IxEventType.QUICK_TRADE]: new Set(),
    [IxEventType.ERROR]: new Set(),
    [IxEventType.WALLET_CONNECTED]: new Set(),
    [IxEventType.WALLET_DISCONNECTED]: new Set(),
    [IxEventType.DEBUG]: new Set(),
    [IxEventType.INFO]: new Set(),
    [IxEventType.AUTHENTICATED]: new Set(),
    [IxEventType.SIWE_AUTHENTICATING]: new Set(),
    [IxEventType.WEBSOCKET_CONNECTED]: new Set(),
    [IxEventType.WEBSOCKET_DISCONNECTED]: new Set(),
    [IxEventType.CHAIN_SWITCHED]: new Set(),
    [IxEventType.UNSUPPORTED_NETWORK]: new Set(),
    [IxEventType.INITIALIZED]: new Set(),
    [IxEventType.SIGN_JSON_RPC_REQUEST]: new Set(),
  };

  // Subscribers that want to receive all events
  private readonly globalSubscribers: Set<(event: IxEvents[keyof IxEvents]) => void> = new Set();

  private valueToKey = (
    value: IxEventType | keyof typeof IxEventType,
  ): keyof typeof IxEventType => {
    // If already a key (uppercase), return directly
    if (value in this.subscribers) {
      return value as keyof typeof IxEventType;
    }
    // Otherwise search by enum value
    const found = (Object.keys(IxEventType) as Array<keyof typeof IxEventType>).find(
      k => IxEventType[k] === value,
    );
    if (!found) {
      throw new Error(`Unsupported event type: ${String(value)}`);
    }
    return found;
  };

  // ------------------
  // Event subscription
  // ------------------
  /**
   * Subscribe to SDK events.
   * Returns a disposal function to remove the listener.
   */
  // Overload – specific event type
  public subscribe<T extends keyof typeof IxEventType>(
    eventName: T,
    subscriber: (event: IxEvents[(typeof IxEventType)[T]]) => void,
  ): () => void;
  // Overload – all events
  public subscribe(eventName: 'all', subscriber: (event: IxEvent) => void): () => void;
  // Implementation
  public subscribe(
    eventName: keyof typeof IxEventType | 'all',
    subscriber: ((event: IxEvent) => void) | ((event: IxEvent | BaseEvent) => void),
  ): () => void {
    if (eventName === 'all') {
      this.globalSubscribers.add(subscriber);
      return () => this.unsubscribe('all', subscriber);
    }

    const key = IxEventType[eventName];

    this.subscribers[key].add(subscriber);

    return () => this.unsubscribe(key, subscriber);
  }

  // ------------------
  // Event unsubscription
  // ------------------
  /**
   * Remove an event listener added with `subscribe`.
   */
  // Overload – all events
  public unsubscribe(eventName: 'all', subscriber: (event: IxEvents[keyof IxEvents]) => void): void;
  // Overload – specific event type
  public unsubscribe<T extends IxEventType>(
    eventName: T,
    subscriber: (event: IxEvents[T]) => void,
  ): void;
  // Implementation
  public unsubscribe(
    eventName: keyof IxEvents | 'all',
    subscriber:
      | ((event: IxEvents[keyof IxEvents]) => void)
      | ((event: IxEvents[keyof IxEvents] | BaseEvent) => void),
  ): void {
    if (eventName === 'all') {
      this.globalSubscribers.delete(subscriber);
      return;
    }

    const key = this.valueToKey(eventName);
    this.subscribers[key].delete(subscriber);
  }

  /**
   * Internal event dispatcher called by SDK modules.
   * It forwards the event to both specific subscribers and global subscribers.
   */
  public dispatchEvent = (event: IxEvent): void => {
    console.log('[SdkClient] Dispatching event:', event);
    // Dispatch to type-specific subscribers – map enum value to key name
    const subscribers = this.subscribers[event.type];

    subscribers?.forEach(handler => handler(event));

    // Dispatch to global subscribers
    this.globalSubscribers.forEach(handler => handler(event));
  };

  /**
   * Construct a new SDK client. All modules are eagerly instantiated and share the same auth state.
   */
  constructor({ env = 'testnet', defaultChainId, defaultCurrency, defaultToken }: SdkConfig) {
    this.env = env;

    this.executeOrder = (args: OrderExecutorArgs) =>
      executeOrder(this, args, { onMessage: this.dispatchEvent });

    this.baseUrl =
      env === 'mainnet'
        ? BASE_URL_MAINNET
        : env === 'dev'
        ? BASE_URL_DEV
        : env === 'staging'
        ? BASE_URL_STAGING
        : BASE_URL_DEV;
    const appKey = env === 'mainnet' ? SOCKET_APP_KEY_MAINNET : SOCKET_APP_KEY_TESTNET;
    const host =
      env === 'mainnet'
        ? SOCKET_HOST_MAINNET
        : env === 'dev'
        ? SOCKET_HOST_DEV
        : SOCKET_HOST_TESTNET;
    this.notification = new NotificationClient(appKey, host, env, this.dispatchEvent);

    this.apiBaseUrl =
      env === 'mainnet'
        ? API_ENDPOINT_MAINNET
        : env === 'dev'
        ? API_ENDPOINT_DEV
        : env === 'staging'
        ? API_ENDPOINT_STAGING
        : API_ENDPOINT_TESTNET;
    const supportedChains = env === 'mainnet' ? mainnetChains : testnetChains;
    let defaultChain = supportedChains.find(chain => chain.id === defaultChainId);
    if (!defaultChain) {
      console.warn('Default chain not found. Using first supported chain', defaultChainId);
      defaultChain = supportedChains[0];
    }

    this.publicAxios = createPublicAxios(this.apiBaseUrl);
    // -----------------------------
    // Public environment variables
    // -----------------------------
    const isMainnet = env === 'mainnet';
    const WEB3_PROJECT_ID = '3a04d38134c46085917b81c1494b1716';
    const ALCHEMY_API_KEY = isMainnet
      ? 'wTfmlKGO5aNipcfLO8gwxKcSeBSBxBry'
      : 'hntMXgOHf-CwMij-j6_pxYHble56eXxm';
    const ALCHEMY_GAS_POLICY_ID = isMainnet
      ? '5e01a515-b24a-409e-af53-2ebcf981752e'
      : 'be956f70-1c84-4b5f-a089-7040e2b165b5';
    const CLARITY_PROJECT_ID = isMainnet ? 'rgw0v7pzbl' : 'qo9nlaj9l4';
    const SPONSORED_CHAINS = isMainnet ? mainnetChainsSponsored : testnetChainsSponsored;

    this.environment = {
      ENV: env,
      WEB3_PROJECT_ID,
      ALCHEMY_API_KEY,
      ALCHEMY_GAS_POLICY_ID,
      API_BASE_URL: this.apiBaseUrl,
      BASE_URL: this.baseUrl,
      SOCKET_APP_KEY: appKey,
      SOCKET_HOST: host,
      CLARITY_PROJECT_ID,
      SPONSORED_CHAINS,
    };

    // Admin endpoints should always point to mainnet when env is "staging"

    this.auth = new Auth(
      supportedChains,
      this.notification,
      this.apiBaseUrl,
      defaultChain.id,
      this.publicAxios,
      this.environment,
    );
    this.config = new ConfigService(this.auth, {
      defaultChainId: defaultChain.id,
      defaultCurrency,
      defaultToken,
    });
    this.ads = new AdsService(this.auth, this.config);
    this.orders = new orderService(this.auth);
  }
}

export { privateKeyToAccount };
export { arbitrumSepolia };

// Public Errors API (import as: import { Errors } from "@sdk";)
export * as Errors from './errors';
